import axios from "@/utils/axios";
import { NodeTypes, type ApiNode } from "@/workflow_api/types/core";
import { useToast } from "@elaniintc/goes-design-system";
import { useMutation, useQuery } from "@tanstack/react-query";

export const useGetNode = () => {
  const toast = useToast();
  return useMutation({
    mutationFn: async (nodeId: string): Promise<ApiNode> => {
      const response = await axios.get(
        `/admin/v1/node/${nodeId}?relationships=${[
          "inputs",
          "inputs.conditions",
          "inputs.calculations",
          "inputs.filterConfiguration.conditions",
          "expiration",
          "expiration.nodeTimeExtension",
          "inputs.sourceConnections",
          "inputs.targetConnections",
        ].join(",")}`,
      );

      const json = response.data?.data;

      return json;
    },
    onError() {
      toast.showToast({
        title: "Error al obtener nodo",
        description: "Ocurrió un error al intentar obtener el nodo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

const getNodeRelationshipsByType = (type: string) => {
  switch (type) {
    case NodeTypes.IF:
    case NodeTypes.SWITCH: {
      return [
        "inputs.conditions",
        "inputs.conditions.input.sourceConnections.field",
        "inputs.conditions.input.targetConnections.field",
        "inputs.conditions.input.references",
        "inputs.conditions.input.testNodeInput",
        "inputs.conditions.optionalInput",
        "inputs.conditions.optionalInput.sourceConnections.field",
        "inputs.conditions.optionalInput.targetConnections.field",
        "inputs.conditions.optionalInput.references",
        "inputs.conditions.optionalInput.testNodeInput",
      ];
    }
    case NodeTypes.CALCULATED: {
      return [
        "inputs.calculations",
        "inputs.calculations.inputs.sourceConnections.field",
        "inputs.calculations.inputs.targetConnections.field",
        "inputs.calculations.inputs.testNodeInput",
      ];
    }
    case NodeTypes.SIGN_DOCUMENT: {
      return [
        "inputs.documents.inputs",
        "inputs.documents.inputs.sourceConnections",
        "inputs.sourceConnections.field",
        "inputs.targetConnections.field",
      ];
    }
    // Eager-load relationships for payment and document-generator nodes
    case NodeTypes.EXPEDIENTE:
    case NodeTypes.RESOLUTION:
    case NodeTypes.NOTIFICATION:
    case NodeTypes.PAYMENT:
    case NodeTypes.DOC_GENERATOR:
    case NodeTypes.N8N:
    case NodeTypes.INTEROPERABILITY: {
      return [
        "inputs.sourceConnections.field",
        "inputs.targetConnections.field",
      ];
    }
    case NodeTypes.FORM:
    case NodeTypes.CORRECTION: {
      return ["expiration", "expiration.nodeTimeExtension"];
    }
    case NodeTypes.DATA_CENTER: {
      return [
        "inputs.filterConfiguration.conditions",
        "inputs.sourceConnections.field",
        "inputs.targetConnections.field",
      ];
    }
    case NodeTypes.WAIT:
    case NodeTypes.MERGE: {
      return ["edges"];
    }
    case NodeTypes.VARIABLE_REPLACEMENT: {
      return [
        "inputs.grouping",
        "inputs.grouping.candidates",
        "inputs.grouping.sourceConnections",
      ];
    }
    default: {
      return [];
    }
  }
};

export const useGetNodeData = (
  id: string,
  type: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["admin-node-id", id],
    queryFn: async (): Promise<ApiNode> => {
      const response = await axios.get(
        `/admin/v1/node/${id}?relationships=${[
          "inputs",
          "inputs.testNodeInput",
          ...getNodeRelationshipsByType(type),
          "inputs.targetConnections",
          "inputs.sourceConnections",
          "inputs.grouping",
          "inputs.grouping.candidates",
          "inputs.grouping.candidates.sourceConnections",
          "targetConnections",
          "sourceConnections",
        ].join(",")}`,
      );

      const json = response.data?.data;

      return json;
    },

    enabled: !!id && enabled,
  });
};

export const useGetNodeDataAsMutation = () => {
  return useMutation({
    mutationFn: async ({
      id,
      type,
    }: {
      id: string;
      type: string;
    }): Promise<ApiNode> => {
      const response = await axios.get(
        `/admin/v1/node/${id}?relationships=${[
          "inputs",
          "inputs.testNodeInput",
          ...getNodeRelationshipsByType(type),
          "inputs.targetConnections",
          "inputs.sourceConnections",
          "targetConnections",
          "sourceConnections",
          "edges",
          "edge",
        ].join(",")}`,
      );

      const json = response.data?.data;

      return json;
    },
  });
};
